import { useCallback, useMemo } from "react"

import { useLocalStorageState } from "src/hooks/use_local_storage_state"

/**
 * Hook to manage column visibility with localStorage persistence.
 * Matches DataTable's useManageTableColumns behavior.
 *
 * @param {Object} options
 * @param {Array} options.columns - Column definitions with key, header, hide, etc.
 * @param {string} options.name - Table name for localStorage key
 * @returns {[Array, Function]} - [columnsWithVisibility, toggleColumn]
 */
export const useColumnVisibility = ({ columns, name }) => {
  const [storedColumns, setStoredColumns] = useLocalStorageState(
    name,
    "columns"
  )

  // Merge stored visibility with column definitions
  const columnsWithVisibility = useMemo(() => {
    return columns.map((col) => {
      const storedColumn = storedColumns?.find((stored) =>
        Object.prototype.hasOwnProperty.call(stored, col.key)
      )

      return {
        ...col,
        hide: storedColumn ? storedColumn[col.key] : col.hide || false,
      }
    })
  }, [columns, storedColumns])

  // Toggle a column's visibility
  const toggleColumn = useCallback(
    (columnKey) => {
      const updatedColumns = columnsWithVisibility.map((col) => {
        if (col.key === columnKey) {
          return { ...col, hide: !col.hide }
        }
        return col
      })

      // Store only the visibility state
      const columnsToStore = updatedColumns.map(({ key, hide }) => ({
        [key]: hide,
      }))
      setStoredColumns(columnsToStore)
    },
    [columnsWithVisibility, setStoredColumns]
  )

  return [columnsWithVisibility, toggleColumn]
}
